import Vue from 'vue'
import Router from 'vue-router'
import { sync } from 'vuex-router-sync'
// eslint-disable-next-line import/extensions
import store from '~/store'
import { configSettings } from '~/plugins/config-settings.js'
// eslint-disable-next-line
import { pathToRegexp } from 'path-to-regexp'
import routes from './routes.js'
import CONSTANTS from '../global_helper/constants.js'

Vue.use(Router)

// The middleware for every page of the application.
const globalMiddleware = ['check-auth']

// Load middleware modules dynamically.
// eslint-disable-next-line no-use-before-define
const routeMiddleware = resolveMiddleware(require.context('~/middleware', false, /.*\.js$/))

// eslint-disable-next-line no-use-before-define
const router = createRouter()

sync(store, router)

export default router

async function afterEach() {
	await router.app.$nextTick()
	router.app.$loading.finish()
}

function callMiddleware(middleware, to, from, next) {
	const stack = middleware.reverse()

	const nextRoute = (...args) => {
		// Stop if "_next" was called with an argument or the stack is empty.
		if (args.length > 0 || stack.length === 0) {
			if (args.length > 0) {
				router.app.$loading.finish()
			}

			return next(...args)
		}

		const middleware = stack.pop()

		if (typeof middleware === 'function') {
			middleware(to, from, nextRoute)
		} else if (routeMiddleware[middleware]) {
			routeMiddleware[middleware](to, from, nextRoute)
		} else {
			throw Error(`Undefined middleware [${middleware}]`)
		}
		return 1
	}

	nextRoute()
}

function resolveComponents(components) {
	return Promise.all(components.map(component => (typeof component === 'function' ? component() : component)))
}

function getMiddleware(components) {
	const middleware = [...globalMiddleware]

	components
		.filter(c => c.middleware)
		.forEach(component => {
			if (Array.isArray(component.middleware)) {
				middleware.push(...component.middleware)
			} else {
				middleware.push(component.middleware)
			}
		})

	return middleware
}

function scrollBehavior(to, from, savedPosition) {
	if (savedPosition) {
		return savedPosition
	}

	if (to.hash) {
		return { selector: to.hash }
	}

	const [component] = router.getMatchedComponents({ ...to }).slice(-1)

	if (component && component.scrollToTop === false) {
		return {}
	}

	return { x: 0, y: 0 }
}

function resolveMiddleware(requireContext) {
	return requireContext
		.keys()
		.map(file => [file.replace(/(^.\/)|(\.js$)/g, ''), requireContext(file)])
		.reduce((guards, [name, guard]) => ({ ...guards, [name]: guard.default }), {})
}

// Add App Install top banner
function handleAppMetaTag(to) {
	const shouldShowBanner = to.meta && to.meta.showAppBanner
	const existingMeta = document.querySelector('meta[name="apple-itunes-app"]')
	const user = store.getters['Auth/user']
	const hasChallenge = user?.experiments?.steven_bartlett_challenge_experiment === 1

	if (existingMeta && hasChallenge) {
		existingMeta.remove()
	} else if (shouldShowBanner && !existingMeta && !hasChallenge) {
		const meta = document.createElement('meta')
		meta.setAttribute('name', 'apple-itunes-app')
		meta.setAttribute('content', 'app-id=6478343472')
		document.head.appendChild(meta)
	}
}

function isMigratedPath(path) {
	return CONSTANTS.MIGRATED_ROUTES.some(pattern => {
		// Handle wildcard patterns manually for better matching
		if (pattern.endsWith('/*')) {
			const basePattern = pattern.slice(0, -2) // Remove '/*'
			return path === basePattern || path.startsWith(`${basePattern}/`)
		}

		// Use pathToRegexp for dynamic routes like :paymentId
		const regexp = pathToRegexp(pattern).regexp
		return regexp.test(path)
	})
}

async function beforeEach(to, from, next) {
	store.commit('Global/setBreadcrumbs', [])
	store.commit('Stores/setPreviewTheme', null)
	store.commit('Global/setPageURL', '')
	let components = []

	// This is for app navigation to v2 app
	if (isMigratedPath(to.path)) {
		window.location.href = `${configSettings.v2AppUrl}${to.path}`
		return
	}

	try {
		// Get the matched components and resolve them.
		components = await resolveComponents(router.getMatchedComponents({ ...to }))
	} catch (error) {
		if (/^Loading( CSS)? chunk (\d)+ failed\./.test(error.message)) {
			window.location.reload(true)
			return
		}
	}

	if (components.length === 0) {
		next()
	}
	const editingRoutes = ['linksite_edit_page']
	const skipModal = to.query.skipModal === 'true'
	const hasUnsavedChanges = store.getters['Global/getUnsavedProductChanges']
	if (!skipModal && editingRoutes.includes(from.name) && hasUnsavedChanges) {
		router.app.$root.$emit('confirm-leave', next)
	}

	// Start the loading bar.
	else {
		if (components && components[components.length - 1] && components[components.length - 1].loading !== false) {
			// router.app.$nextTick(() => router.app.$loading.start())
		}

		// Get the middleware for all the matched components.
		const middleware = getMiddleware(components)

		// Check if the route requires the app banner and add it if so
		handleAppMetaTag(to)

		// Call each middleware.
		callMiddleware(middleware, to, from, (...args) => {
			next(...args)
		})
	}
}

function createRouter() {
	const router = new Router({
		scrollBehavior,
		mode: 'history',
		routes: [...routes],
	})

	router.beforeEach(beforeEach)
	router.afterEach(afterEach)

	return router
}
